<script setup lang="ts">
  import { AlignJustify, Check, Clipboard, Download, MessageCircle } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import CardWidthToggle from "./CardWidthToggle.vue";

  interface ToolbarCard {
    id: number;
    card_type?: string;
    content?: { sub_type?: string };
    options?: { width?: string } | null;
  }

  const props = withDefaults(
    defineProps<{
      card: ToolbarCard;
      displayMode: string;
      readonly?: boolean;
      copied?: boolean;
      showVerbatims?: boolean;
      showSummary?: boolean;
      showCopy?: boolean;
      showDownload?: boolean;
    }>(),
    {
      readonly: false,
      copied: false,
      showVerbatims: false,
      showSummary: false,
      showCopy: false,
      showDownload: false,
    },
  );

  const emit = defineEmits<{
    (e: "set-mode", mode: string): void;
    (e: "copy"): void;
    (e: "download"): void;
  }>();

  const modeClass = (mode: string) =>
    props.displayMode === mode ? "bg-muted text-foreground" : "text-muted-foreground";
</script>

<template>
  <div class="card-toolbar flex shrink-0 items-center gap-1">
    <slot />
    <Tooltip
      v-if="showVerbatims"
      content="Verbatims"
      :delay-duration="1000">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Verbatims view"
        :class="modeClass('verbatims')"
        @click="emit('set-mode', 'verbatims')">
        <MessageCircle class="size-3.5" />
      </Button>
    </Tooltip>
    <Tooltip
      v-if="showSummary"
      content="Summary"
      :delay-duration="1000">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Summary view"
        :class="modeClass('summary')"
        @click="emit('set-mode', 'summary')">
        <AlignJustify class="size-3.5" />
      </Button>
    </Tooltip>
    <CardWidthToggle
      :card="card"
      :readonly="readonly" />
    <Tooltip
      v-if="showCopy"
      :content="copied ? 'Copied!' : 'Copy image'"
      :delay-duration="1000">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Copy as image"
        class="text-muted-foreground"
        @click="emit('copy')">
        <Check
          v-if="copied"
          class="size-3.5 text-status-success-strong" />
        <Clipboard
          v-else
          class="size-3.5" />
      </Button>
    </Tooltip>
    <Tooltip
      v-if="showDownload"
      content="Download image"
      :delay-duration="1000">
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label="Download as image"
        class="text-muted-foreground"
        @click="emit('download')">
        <Download class="size-3.5" />
      </Button>
    </Tooltip>
  </div>
</template>
